import { CSSProperties } from "react";
import { AllowEmptyDlfMatcher } from "../../models/matchers/AllowEmptyDlfMatcher";
import { DlfMatcher } from "../../models/matchers/DlfMatcher";
import { OcrConfidenceThresholdSliderButton } from "../buttons/OcrConfidenceThresholdSliderButton";
import { OnOffToggle } from "../buttons/OnOffToggle";
import { SliderButton } from "../buttons/SliderButton";
import { FuzzyRange } from "../fuzzy/FuzzyRange";
import Icon from "../Icon";
import { RangeInput } from "../inputs/Range";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import {
  TTOcrConfidenceThreshold,
  TTTemplateOcrConfidenceThreshold,
} from "../tooltips/ConfigurationTooltips";
import {
  TTAllowEmpty,
  TTConfidenceLabel,
  TTConfidenceThreshold,
  TTReusableButton,
} from "../tooltips/TTDocumentFieldConfiguration";

export {
  // Main export
  DlfMatcherToggles,
  FuzzyThreshold,
};
type Props = {
  matcher: DlfMatcher;
  /**
   * If displayed, current extraction confidence will be displayed next to the
   */
  extractionConfidence?: number;
  ocrConfidence?: number;
  /**
   * If defined, ocr confidence threhsold slider shown
   */
  defaultOcrConfidence?: number;
  // showOcrConfidence?: boolean;
  updateMatcher: (matcher: DlfMatcher) => void;
};

/**
 * Render Allow Empty & Reusable toggle buttons for supported DLFMatchers. Render a Matcher Threshold slider for
 * KeyValueMatchers, and displays extraction confidence if available.
 * @param props
 * @returns
 */
const DlfMatcherToggles = (props: Props) => {
  const confidenceError = props.extractionConfidence
    ? props.matcher.threshold > props.extractionConfidence
    : false;
  const extractionConfidenceStyle: CSSProperties = {
    marginLeft: "2.5rem",
  };
  if (confidenceError) {
    extractionConfidenceStyle["fontWeight"] = "bold";
    extractionConfidenceStyle["color"] = "red";
  }

  return (
    <>
      {/* Allow Empty toggle */}
      {props.matcher.supportsAllowEmpty() && (
        <AdvancedTooltip
          overflow
          tooltipText={TTAllowEmpty}
          possiblePlacements={["bottom-center"]}
        >
          {(tProps) => (
            <OnOffToggle
              tProps={tProps}
              active={(props.matcher as AllowEmptyDlfMatcher).getAllowEmpty()}
              onClick={() => {
                (props.matcher as AllowEmptyDlfMatcher).toggleAllowEmpty();
                props.updateMatcher(props.matcher);
              }}
            >
              Allow Empty
            </OnOffToggle>
          )}
        </AdvancedTooltip>
      )}
      {/* TODO: add ReusableDlfMatchers and supportsReusable() */}
      {/* Reusable toggle for non DefaultValueMather */}
      {props.matcher.getDiscriminator() !== "F_DEFAULT" && (
        <AdvancedTooltip
          overflow
          tooltipText={TTReusableButton}
          possiblePlacements={["bottom-center"]}
        >
          {(tProps) => (
            <OnOffToggle
              tProps={tProps}
              active={props.matcher.getReusable()}
              onClick={() => {
                props.matcher.toggleReusable();
                props.updateMatcher(props.matcher);
              }}
            >
              Reusable
            </OnOffToggle>
          )}
        </AdvancedTooltip>
      )}
      {/* OCR Confidence Threshold button */}
      {props.matcher.getDiscriminator() !== "F_DEFAULT" && (
        <AdvancedTooltip
          short
          overflow
          tooltipText={TTTemplateOcrConfidenceThreshold}
          possiblePlacements={["bottom-center"]}
        >
          {(tProps) => (
            <>
              <span
                {...tProps}
                data-name={"ocrConfidenceThreshold"}
                style={{ marginLeft: "1.5rem", marginRight: "0.5rem" }}
              >
                OCR Confidence Threshold:{" "}
              </span>

              <OcrConfidenceThresholdSliderButton
                default={props.defaultOcrConfidence ?? 90}
                threshold={props.matcher.ocrConfidenceThreshold}
                update={(threshold) => {
                  props.matcher.ocrConfidenceThreshold = threshold;
                  props.updateMatcher(props.matcher);
                }}
              />
            </>
          )}
        </AdvancedTooltip>
      )}
      {/* OCR Confidence. Displayed as 0.00% if less than 0 */}
      {props.matcher.getDiscriminator() !== "F_DEFAULT" &&
        props.ocrConfidence != undefined && (
          <AdvancedTooltip
            short
            overflow
            tooltipText={TTTemplateOcrConfidenceThreshold}
            possiblePlacements={["bottom-center"]}
          >
            {(tProps) => (
              <span
                {...tProps}
                style={{
                  marginLeft: "2.5rem",
                }}
              >
                OCR Confidence:{" "}
                {parseFloat(Math.max(props.ocrConfidence ?? 0, 0) + "").toFixed(
                  2,
                )}
                %
              </span>
            )}
          </AdvancedTooltip>
        )}
      {/* KeyValueMatcher Confidence button */}
      {props.matcher.getDiscriminator() === "F_KEYVALUE" && (
        // show Threshold slider
        <AdvancedTooltip
          overflow
          tooltipText={TTConfidenceThreshold}
          possiblePlacements={["bottom-center"]}
        >
          {(tProps) => (
            <>
              <span
                {...tProps}
                style={{ marginLeft: "1.5rem", marginRight: "0.5rem" }}
              >
                Confidence Threshold:{" "}
              </span>
              <FuzzyThreshold
                threshold={props.matcher.threshold}
                update={(threshold) => {
                  props.matcher.threshold = threshold;
                  props.updateMatcher(props.matcher);
                }}
              />
            </>
          )}
        </AdvancedTooltip>
      )}
      {/* KeyValueMatcher extraction confidence */}
      {props.matcher.getDiscriminator() === "F_KEYVALUE" &&
        props.extractionConfidence != undefined && (
          <AdvancedTooltip
            overflow
            tooltipText={TTConfidenceLabel}
            possiblePlacements={["bottom-center"]}
          >
            {(tProps) => (
              <span {...tProps} style={extractionConfidenceStyle}>
                Confidence:{" "}
                {parseFloat(props.extractionConfidence + "").toFixed(2)}%
              </span>
            )}
          </AdvancedTooltip>
        )}
    </>
  );
};

type FuzzyThresholdProps = {
  threshold: number;
  update: (value: number) => void;
};
function FuzzyThreshold(props: FuzzyThresholdProps) {
  return (
    <SliderButton
      label={""}
      buttonChildrenFn={(value) => (
        <>
          {value + "%"}
          <Icon altText={"Change Fuzzy threshold"} icon={"cog"} />
        </>
      )}
      buttonClassName={"white fuzzyThreshold"}
      rangeInputChildren={(value, onMove, onChange) => (
        <FuzzyRange
          label={"Fuzzy"}
          value={value}
          inputLabel
          onChange={onChange}
          onMove={onMove}
        />
      )}
      threshold={props.threshold}
      update={props.update}
    />
  );
}
